import axios from 'axios';
import { ActionTree } from 'vuex';
import { RootState } from '@/store/store';
import { UploadVideoState } from '@/store/stores/upload-video.store';
import {
  mutationVideoIsUploading,
  mutationUploadingStatus,
  mutationStartRecord,
  mutationSetDocumentId,
  mutationVideoIsSaving,
  mutationSetVideoId,
  mutationResetUploadVideo,
  mutationSetVideoStreamStatus,
} from '@/store/mutations/upload-video.mutations';
import { IVideoResponse } from '@/types/responses/VideoResponse';
import { IVideo } from '@/types/Video';

export const actionCheckVideoJob = 'checkVideoJob';
export const actionStartRecord = 'startRecord';
export const actionSetVideo = 'setVideo';
export const actionResetUploadVideo = 'cancelVideoUpload';
export const actionSetVideoStreamStatus = 'setVideoStreamStatus';
export const actionUploadVideo = 'uploadVideo';
export const actionUploadVideoSuccess = 'uploadVideoSuccess';
export const actionUploadVideoFailed = 'uploadVideoFailed';
export const actionGetVideoDetails = 'getVideoDetails';
export const actionAddToLibrary = 'addVideoToLibrary';
export const actionSendAppInvite = 'sendAppInvite';

export enum VideoUploadingStatus {
  saving = 'saving',
  saved = 'saved',
  complete = 'complete',
  failed = 'failed',
  success = 'success',
}

export const actions: ActionTree<UploadVideoState, RootState> = {
  async [actionCheckVideoJob]({ commit }, videoId) {
    commit(mutationVideoIsUploading, true);
    const body = new FormData();
    body.set('id', videoId);
    videoJobStatus(body, commit, 0);
  },
  async [actionStartRecord]({ commit }, documentId) {
    commit(mutationStartRecord, true);
    commit(mutationSetDocumentId, documentId);
  },
  async [actionSetVideo]({ commit }, { documentId, videoId }) {
    commit(mutationVideoIsSaving, VideoUploadingStatus.saving);
    const body = new FormData();
    body.set('document-id', documentId);
    body.set('video-id', videoId);
    const {
      data: { status },
    } = await axios.post('/save/video', body);
    if (status === 1) {
      commit(mutationSetVideoId, videoId);
      commit(mutationUploadingStatus, VideoUploadingStatus.saved);
    } else {
      commit(mutationUploadingStatus, VideoUploadingStatus.failed);
    }
  },
  async [actionResetUploadVideo]({ commit }) {
    commit(mutationResetUploadVideo, true);
  },
  async [actionSetVideoStreamStatus]({ commit }, status) {
    commit(mutationSetVideoStreamStatus, status);
  },
  async [actionUploadVideo](
    { commit, dispatch },
    { file, filename }: { file: File; filename: string }
  ) {
    commit(mutationVideoIsSaving, VideoUploadingStatus.saving);
    const formData = new FormData();
    formData.append('file', file, filename);
    const { data } = await axios.post('/save/video', formData);
    if (data.status === 1) {
      dispatch('uploadVideoSuccess', data.id);
    } else {
      dispatch('uploadVideoFailed');
    }
  },
  async [actionUploadVideoFailed]({ commit }) {
    commit(mutationVideoIsUploading, false);
    commit(mutationUploadingStatus, VideoUploadingStatus.failed);
  },
  async [actionUploadVideoSuccess]({ commit }, videoId: string) {
    commit(mutationSetVideoId, videoId);
    commit(mutationUploadingStatus, VideoUploadingStatus.complete);
    commit(mutationVideoIsUploading, false);
  },
  async [actionGetVideoDetails](context, { videoId }: { videoId: string }) {
    const body = new FormData();
    body.append('video-id', videoId);
    const {
      data: { video },
    } = await axios.post(`/get/video/details`, body);
    return convertVideosReponse(video);
  },
  async [actionAddToLibrary](context, videoId: string) {
    const body = new FormData();
    body.append('action', 'saved');
    body.append('saved', '1');
    body.append('video-id', videoId);

    const {
      data: { status },
    } = await axios.post(`/save/video`, body);
    return status === 1;
  },
  async [actionSendAppInvite](
    context,
    {
      mobileNumber,
      userId,
    }: {
      mobileNumber: string;
      userId: string;
    }
  ) {
    const body = new FormData();
    body.append('action', 'sendapp');
    body.append('mobile', mobileNumber);
    body.append('user-id', userId);

    const {
      data: { status },
    } = await axios.post(`/save/user`, body);
    return status === 1;
  },
};

const videoJobStatus = async (body, commit, laps) => {
  if (laps >= 5) {
    return false;
  }
  const {
    data: { Status },
  } = await axios.post('/get/video/job', body);
  commit(mutationUploadingStatus, Status);
  if (Status !== 'Complete') {
    return videoJobStatus(body, commit, laps++);
  }
  commit(mutationVideoIsUploading, false);
};

const convertVideosReponse = (videos: IVideoResponse[]): IVideo[] => {
  return videos.map(
    (video: IVideoResponse) =>
      ({
        editable: video.editable,
        published: video.published,
        saved: video.saved,
        thumbUrl: video.thumb_url,
        videoId: video.video_id,
        videoTitle: video.video_title,
        videoType: video.video_type,
        videoUrl: video.video_url,
      } as IVideo)
  );
};
